// Catalogue des pièces (tétracubes + pentacubes) et palettes de thèmes.
// Palette néon/énergie originale : teintes vives réparties sur la roue.
// Données pures — aucune dépendance à l'état du jeu ni à Three.js.
// minLevel : palier de Profondeur à partir duquel la pièce entre dans le sac (≥).
// Absent = disponible dès le départ. Les pentacubes pleinement 3D (qui s'étirent
// sur trois axes / plusieurs étages) sont durs à poser : on ne les introduit
// qu'après la Profondeur 4 pour densifier le défi sans noyer le début de partie.
export interface PieceDef { name: string; color: number; cells: number[][]; minLevel?: number; }
export interface ThemeDef { bg: string[]; deep: string; fog: number; accent: number; grid: number[]; }

// Cadence d'introduction des pièces complexes : la première apparaît APRÈS la
// Profondeur 4 (donc à la Profondeur 5), puis UNE NOUVELLE toutes les 2 Profondeurs
// (5, 7, 9, 11, 13, 15, 17…). Le vocabulaire de pièces s'enrichit en douceur au lieu de
// tout lâcher d'un coup → la difficulté monte par la complexité, pas par la montée du sol.
// La cadence est PROLONGÉE en profondeur (BAL-LATEGAME-001) pour qu'un expert continue
// d'être mis sous pression au-delà de la Prof. 11 (où elle s'arrêtait auparavant).
export const COMPLEX_BASE_LEVEL = 5;   // palier de la 1re pièce complexe
export const COMPLEX_STEP = 2;         // +1 pièce complexe toutes les N Profondeurs

export const PIECES: PieceDef[] = [
  {name:'I',color:0xff2db7,cells:[[0,0,0],[1,0,0],[2,0,0],[3,0,0]]},
  {name:'O',color:0x2f6bff,cells:[[0,0,0],[1,0,0],[0,0,1],[1,0,1]]},
  {name:'L',color:0x1ef0c8,cells:[[0,0,0],[1,0,0],[2,0,0],[2,0,1]]},
  {name:'J',color:0x9bff3d,cells:[[0,0,0],[1,0,0],[2,0,0],[0,0,1]]},
  {name:'T',color:0xff5a4d,cells:[[0,0,0],[1,0,0],[2,0,0],[1,0,1]]},
  {name:'S',color:0xd24bff,cells:[[0,0,0],[1,0,0],[1,0,1],[2,0,1]]},
  {name:'Tripod',color:0xffd42b,cells:[[0,0,0],[1,0,0],[0,1,0],[0,0,1]]},
  {name:'Screw',color:0x19c3ff,cells:[[0,0,0],[1,0,0],[1,0,1],[1,1,1]]},
  {name:'I5',color:0xff2d5e,cells:[[0,0,0],[1,0,0],[2,0,0],[3,0,0],[4,0,0]]},
  {name:'L5',color:0xd6ff2b,cells:[[0,0,0],[1,0,0],[2,0,0],[3,0,0],[3,0,1]]},
  {name:'T5',color:0x6a5cff,cells:[[0,0,0],[1,0,0],[2,0,0],[1,0,1],[1,0,2]]},
  {name:'P5',color:0xff4fa3,cells:[[0,0,0],[1,0,0],[0,0,1],[1,0,1],[0,0,2]]},
  {name:'U5',color:0xb14bff,cells:[[0,0,0],[0,0,1],[1,0,1],[2,0,1],[2,0,0]]},
  {name:'Plus',color:0x46b4ff,cells:[[1,0,0],[0,0,1],[1,0,1],[2,0,1],[1,0,2]]},
  {name:'Chair',color:0x2ddacf,cells:[[0,0,0],[1,0,0],[0,0,1],[1,0,1],[0,1,0]]},
  {name:'Tower+',color:0xff9b2f,cells:[[0,0,0],[1,0,0],[2,0,0],[1,0,1],[1,1,0]]},
  // ——— Pentacubes complexes (torsions tri-axiales, durs à caler), introduits un par
  // un toutes les 2 Profondeurs à partir de la 5 (cf. COMPLEX_BASE_LEVEL / COMPLEX_STEP).
  // Hélice (Prof. 5) : zigzag montant qui vrille sur les trois axes (x→y→z→x).
  {name:'Helix',color:0xff4f8b,cells:[[0,0,0],[1,0,0],[1,1,0],[1,1,1],[2,1,1]],minLevel:5},
  // Étoile (Prof. 7) : nœud central avec UN bras dans chacune des six directions
  // orthogonales (±X, ±Y, ±Z) — une véritable étoile 3D / octaèdre. 7 cellules.
  {name:'Star3D',color:0x39ffd0,cells:[[1,1,1],[0,1,1],[2,1,1],[1,0,1],[1,2,1],[1,1,0],[1,1,2]],minLevel:7},
  // Escalier (Prof. 9) : marche en colimaçon qui décale en x puis en z d'un étage à l'autre.
  {name:'Stair',color:0xffa524,cells:[[0,0,0],[1,0,0],[1,1,0],[2,1,0],[2,1,1]],minLevel:9},
  // Crochet (Prof. 11) : haute colonne coiffée d'un coude qui plonge en profondeur.
  {name:'Hook',color:0x8a7bff,cells:[[0,0,0],[0,1,0],[0,2,0],[1,2,0],[1,2,1]],minLevel:11},
  // ——— LÉTALITÉ DE FIN DE PARTIE (BAL-LATEGAME-001) : l'enrichissement des pièces s'arrêtait à la
  // Prof. 11 → un expert ne rencontrait plus AUCUNE montée de difficulté (mesuré : il ne meurt jamais).
  // On PROLONGE la cadence (13, 15, 17) avec des pentacubes tri-axiaux durs à caler à plat → sous la
  // pression du temps en profondeur, ils forcent des placements imparfaits, la pile monte, la run finit
  // par le DÉFI (et non la fatigue). Aucune montée d'instabilité (rise gelé) : la difficulté reste
  // portée par la vitesse + la complexité. Pur/déterministe : le sac seedé reste reproductible.
  // Spire (Prof. 13) : chicane qui vrille en escalier sur les trois axes.
  {name:'Spire',color:0x4fd0ff,cells:[[0,0,0],[0,1,0],[1,1,0],[1,1,1],[2,1,1]],minLevel:13},
  // Triskel (Prof. 15) : moyeu à quatre bras orthogonaux (±x, +y, +z) — impossible à poser à plat.
  {name:'Triskel',color:0xff6f4f,cells:[[1,0,0],[0,0,0],[2,0,0],[1,1,0],[1,0,1]],minLevel:15},
  // Crook (Prof. 17) : colonne haute coudée qui plonge d'un cran en profondeur — surplomb garanti.
  {name:'Crook',color:0xc04fff,cells:[[0,0,0],[1,0,0],[1,0,1],[1,1,1],[1,2,1]],minLevel:17},
];

// Indices des pièces tirables à une Profondeur donnée : on exclut les pentacubes
// complexes tant que leur minLevel n'est pas atteint. Pur et déterministe (le sac
// du défi du jour reste reproductible : le niveau découle des lignes effacées).
// PERF-CORE-005 : cache par niveau (l'ensemble ne change qu'à quelques seuils).
// On renvoie une copie fraîche pour ne pas muter le cache lors du mélange seedé.
const _piecesCache = new Map<number, number[]>();
export function availablePieces(level: number): number[] {
  const cached = _piecesCache.get(level);
  if (cached) return cached.slice();
  const out: number[] = [];
  for (let i = 0; i < PIECES.length; i++) {
    const m = PIECES[i].minLevel;
    if (m === undefined || level >= m) out.push(i);
  }
  _piecesCache.set(level, out);
  return out.slice();
}

export const THEMES: ThemeDef[] = [
  {bg:['#16204a','#1a0f33'],deep:'#070912',fog:0x0a0e22,accent:0x7af0ff,grid:[0x5566bb,0x33407a]},
  {bg:['#0a2e2a','#06281f'],deep:'#04110d',fog:0x06150f,accent:0x34f5c5,grid:[0x2f7a6a,0x1e4a44]},
  {bg:['#2a1030','#3a0f1c'],deep:'#140810',fog:0x140810,accent:0xff7a9c,grid:[0x8a4a6a,0x5a2f3a]},
  {bg:['#1a0f3a','#2a1050'],deep:'#0c0620',fog:0x0c0620,accent:0xb98aff,grid:[0x6a4aa0,0x4a3070]},
  {bg:['#2a1606','#321a08'],deep:'#100805',fog:0x100805,accent:0xffc05b,grid:[0x8a6a3a,0x5a4a2a]},
  // Liquide — verre/joaillerie : teal profond, accents perle/aqua, grille douce.
  {bg:['#0d3b39','#08302e'],deep:'#06201f',fog:0x07211f,accent:0x9fe7d8,grid:[0x3f8f86,0x245a54]},
];
